import type { WorkOrderChangeStatusEnum } from 'gql/graphql';
import { getCheckedIdsFromObjValues } from 'utils';

import type { CellClassParams, ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { BimWorkOrderChangeMenuRenderer, type TGroupedTableItem } from '../components';

export const useWorkOrderChangeOtherMenuColumn = ({
    constructionTypeId,
    refreshTable,
    workOrderChangeStatus,
}: {
    constructionTypeId: number | undefined;
    refreshTable: () => void;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
}) => {
    const [isSingleDeleteModalOpened, setIsSingleDeleteModalOpened] = useState(false);
    const [isExcludeModalOpened, setIsExcludeModalOpened] = useState(false);
    const [checkedRows, setCheckedRows] = useState<Record<string, TGroupedTableItem | null> | null>(null);
    const [totalDetailsOpened, setTotalDetailsOpened] = useState(false);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows(null);
    }, []);

    const checkRowHandler = useCallback((id, state) => {
        setCheckedRows((prevState) => ({ ...prevState, [id]: state }));
    }, []);

    const openSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(true);
    }, []);
    const openExcludeModalHandler = useCallback(() => {
        setIsExcludeModalOpened(true);
    }, []);

    const closeSingleModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(false);
        setIsExcludeModalOpened(false);

        setCheckedRows(null);
    }, []);

    const toggleTotalDetails = useCallback(() => {
        setTotalDetailsOpened((prev) => !prev);
    }, []);

    const menuColumn = useMemo<ColDef<TGroupedTableItem>>(
        () => ({
            field: 'menu' as any,
            headerName: '',
            width: 80,
            resizable: false,
            pinned: 'left',
            lockPosition: 'left',
            cellRenderer: BimWorkOrderChangeMenuRenderer,
            cellRendererParams: {
                checkRowHandler,
                checkedRows,
                openSingleDeleteModalHandler,
                constructionTypeId,
                refreshTable,
                toggleTotalDetails,
                workOrderChangeStatus,
                openExcludeModalHandler,
            },
            cellClassRules: {
                bottomPinnedMenuCell: (params: CellClassParams) => params.node.rowPinned === 'bottom',
                centeredAgGridCell: () => true,
            },
        }),
        [
            checkRowHandler,
            checkedRows,
            constructionTypeId,
            openExcludeModalHandler,
            openSingleDeleteModalHandler,
            refreshTable,
            toggleTotalDetails,
            workOrderChangeStatus,
        ],
    );

    const preparedToDelete = useMemo(
        () =>
            getCheckedIdsFromObjValues(['workOrderChangeOtherPriceId', 'workOrderOtherPriceId'])(checkedRows) as {
                workOrderChangeOtherPriceId: number;
                workOrderOtherPriceId: number | null;
            }[],
        [checkedRows],
    );

    return {
        menuColumn,
        resetCheckedRows,
        checkedRows: preparedToDelete,
        isSingleDeleteModalOpened,
        isExcludeModalOpened,
        closeSingleModalHandler,
        totalDetailsOpened,
        toggleTotalDetails,
    };
};
